import { useEntitlementsStore } from '@/stores/useEntitlementsStore';
import { useSessionStore } from '@/stores/useSessionStore';
import ManageUserRepository from '@/repositories/ManageUserRepository';

let backendManageAccess = null;

async function canAccessManageViaBackend() {
  if (backendManageAccess !== null) {
    return backendManageAccess;
  }
  try {
    const { data } = await ManageUserRepository.me();
    backendManageAccess = data?.can_manage === true;
  } catch {
    backendManageAccess = false;
  }
  return backendManageAccess;
}

/**
 * Route guard for the admin portal. Built on the same policy as
 * `useManageAccess` (super-users only in v1).
 *
 * Routes flagged `meta.public: true` (login, access-denied) bypass the gate.
 * Everything else requires an authenticated super-user; otherwise we redirect
 * to login (unauthenticated) or the access-denied screen (authenticated but
 * not permitted).
 *
 * @param {{ authRestore: { waitForInitialAuthCheck: (route: any) => Promise<void> } }} deps
 */
export function createManageAccessGuard({ authRestore }) {
  return async (to) => {
    if (to.meta?.public) {
      return undefined;
    }

    await authRestore.waitForInitialAuthCheck(to);

    const session = useSessionStore();
    const entitlements = useEntitlementsStore();

    if (!session.isAuthenticated) {
      backendManageAccess = null;
      return { name: 'admin-login', query: { redirect: to.fullPath } };
    }

    if (entitlements.isSuperUser) {
      backendManageAccess = true;
      return undefined;
    }

    if (!(await canAccessManageViaBackend())) {
      return { name: 'admin-access-denied' };
    }

    return undefined;
  };
}
